// The theme water as WS_WaterGeomManager draws it (docs/platform/RenderFidelity.md "Water"):
// two `XWaterGeomSet` planes at the water level (`__CreateXomGeometry` 0xe3ea60: x ±13000, the
// back plane z 0..-10000 in `WaterPassBack:DRL_WaterLayer`, the front plane z 0..2500 in
// `WaterPassFront:DRL_WaterLayer`) drawn with Root.System.WSWater{V,F} (the reflective
// geometry: `__CreateGeometry` 0xe405e0) and the uniforms `XomOglDrawWaterGeomSet` 0x11a5b70 sets:
// - `_WaveScale4` = the theme's `m_WaveScale`; `_WaveOffset` = fmod(t · speed · scale, 1) per
//   component with t = `XomGetSystemTimeMilli()` / 1000 / 20 (the frame clock);
// - `_WaterColor` = the theme's `m_DiffuseColor`; fog from `m_FogStart`/`m_FogEnd`/`m_FogColor`;
// - `waterLevel` / `frontLevel` = `ComputeWaterLevelNDC` / `ComputeWaterFrontLevelNDC` 0xe3f680 /
//   0xe3f760: (clip y / w of (0, level, 0) resp. (0, level, 2500) + 1) · 0.5 under the game camera;
// - `_BumpMap` = SuperBundle `Waterbump`, `_NormalMap` = `WaterNMB`, `_Fresnel` =
//   `Water Fresnel_RGB`, `_ReflectionTex` = the `WaterReflection` surface (passes.ts); gdb at
//   `XomOglDrawThreadedWaterGeomSet`'s draw (0x119cd33, hb-A 3100): the three bitmaps repeat and
//   sample linearly without mipmaps (512x512 RGBA8, 512x512 RGB8, 256x32 RGBA8), the reflection
//   (640x360) clamps;
// - blend (SRC_ALPHA, ONE_MINUS_SRC_ALPHA, ONE, ZERO), no depth test or write, no culling.
// The shader writes alpha 0.9, which the front plane's reflection lookup uses to recognise water.
import * as THREE from 'three';
import { assetJson, assetUrl } from '../assets.ts';

const VERT = /* glsl */ `
precision highp float;
in vec3 INvertPosition;
out vec2 bumpuv0;
out vec2 bumpuv1;
out vec3 viewDir;
out vec4 vertWS;
uniform mat4 ModelMatrix;
uniform mat4 ViewProjM;
uniform mat4 ViewMatrix;
uniform vec4 _WaveScale4;
uniform vec4 _WaveOffset;
void main() {
  mat4 MVP = ViewProjM * ModelMatrix;
  gl_Position = MVP * vec4(INvertPosition.xyz, 1.0);
  vec4 temp = (INvertPosition.xzxz * vec4(0.001)) * _WaveScale4 + _WaveOffset;
  bumpuv0 = temp.xy;
  bumpuv1 = temp.wz;
  vertWS = ModelMatrix * vec4(INvertPosition, 1.0);
  viewDir = (ViewMatrix * vertWS).xyz;
}`;

const FRAG = /* glsl */ `
precision highp float;
in vec2 bumpuv0;
in vec2 bumpuv1;
in vec3 viewDir;
in vec4 vertWS;
uniform sampler2D _BumpMap;
uniform sampler2D _NormalMap;
uniform sampler2D _Fresnel;
uniform sampler2D _ReflectionTex;
uniform vec4 _WaterColor;
uniform float fogStart;
uniform float fogEnd;
uniform vec3 fogColor;
uniform float waterLevel;
uniform float frontLevel;
uniform mat4 ViewProjM;
out vec4 OUTColour;
vec3 UnpackNormal(vec4 packednormal) { return packednormal.xyz * 2.0 - 1.0; }
void main() {
  vec2 finalNormal = UnpackNormal(texture(_NormalMap, bumpuv0)).xz;
  vec4 screenCoord = ViewProjM * vertWS;
  screenCoord.xy /= screenCoord.w;
  vec2 NDC = (screenCoord.xy + vec2(1.0, 1.0)) * 0.5;
  vec2 uv1 = NDC;
  float distanceFromReflection = (abs(uv1.y - waterLevel) * 2.0) * finalNormal.y;
  uv1.x = uv1.x + abs(distanceFromReflection * 0.5 / finalNormal.y) * finalNormal.x;
  float isFrontWater = float(NDC.y < waterLevel);
  vec2 uv2 = uv1 - (isFrontWater * 0.005);
  uv1.y = uv1.y + isFrontWater * (distanceFromReflection + 0.005);
  vec4 refl = texture(_ReflectionTex, uv1.xy);
  if (isFrontWater > 0.0) {
    if (refl.a >= 0.89 && refl.a <= 0.91) {
      uv1.y = uv2.y;
      refl = texture(_ReflectionTex, uv1.xy);
    }
  }
  vec3 refr = texture(_ReflectionTex, uv2.xy).xyz * _WaterColor.xyz;
  vec3 bump1 = UnpackNormal(texture(_BumpMap, (vec2(1.0, 1.0) - bumpuv1))).xzy;
  vec3 bump2 = UnpackNormal(texture(_BumpMap, bumpuv1)).xzy;
  vec3 bump = (bump1 + bump2) * 0.5;
  vec3 f_viewDir = normalize(viewDir);
  float base = dot(f_viewDir, bump);
  float fresnel = texture(_Fresnel, vec2(base, base)).r;
  vec3 colorLinear = mix(refr.xyz, refl.xyz, fresnel);
  float fallOff = clamp(isFrontWater * (waterLevel - NDC.y) / (waterLevel - frontLevel), 0.0, 1.0);
  colorLinear = mix(colorLinear.xyz, refr.xyz, fallOff);
  float f = (fogEnd - vertWS.z) / (fogEnd - fogStart);
  f = clamp(f, 0.0, 1.0);
  OUTColour.rgb = mix(fogColor, colorLinear, f);
  OUTColour.a = 0.9;
}`;

type Vec3 = { x: number; y: number; z: number };
type Vec4 = Vec3 & { w: number };
/** The `WS_Environment` water and light fields (wsthemes.thm). */
export type WaterEnv = {
  m_WaveScale: Vec4; m_WaveSpeed: Vec4; m_DiffuseColor: Vec3; m_FogStart: number; m_FogEnd: number; m_FogColor: Vec3;
};

type XomGraph = { graph: { objects: Record<string, { class: string; png?: string | null }> } };

/** An `XBitmapDescriptor`'s image as a raw texture (data rows as stored: row 0 = v 0): linear,
 * no mipmaps. */
async function bitmap(json: string, wrap: THREE.Wrapping): Promise<THREE.Texture> {
  const desc = await assetJson<XomGraph>(json);
  const img = Object.values(desc.graph.objects).find(o => o.class === 'XImage' && o.png);
  if (!img?.png) throw new Error(`${json}: no image`);
  const tex = await new THREE.TextureLoader().loadAsync(assetUrl(img.png));
  tex.flipY = false;
  tex.colorSpace = THREE.NoColorSpace;
  tex.wrapS = tex.wrapT = wrap;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}

/** `XomGetSystemTimeMilli` / 1000 / 20 (the 20.0 at 0x15a98b8). */
function waveTime(frame: number): number {
  return Math.floor((frame * 1000) / 60) / 1000 / 20;
}

export class Water {
  /** `WaterPassBack:DRL_WaterLayer`. */
  back: THREE.Mesh;
  /** `WaterPassFront:DRL_WaterLayer`. */
  front: THREE.Mesh;
  private uniforms: Record<string, THREE.IUniform>;
  private env: WaterEnv | null = null;

  constructor() {
    this.uniforms = {
      ModelMatrix: { value: new THREE.Matrix4() }, ViewProjM: { value: new THREE.Matrix4() }, ViewMatrix: { value: new THREE.Matrix4() },
      _WaveScale4: { value: new THREE.Vector4() }, _WaveOffset: { value: new THREE.Vector4() },
      _BumpMap: { value: null }, _NormalMap: { value: null }, _Fresnel: { value: null }, _ReflectionTex: { value: null },
      _WaterColor: { value: new THREE.Vector4() }, fogStart: { value: 0 }, fogEnd: { value: -10000 }, fogColor: { value: new THREE.Vector3() },
      waterLevel: { value: 0 }, frontLevel: { value: 0 },
    };
    // XWATER_VERTEX positions of __CreateXomGeometry (indices 0 1 2, 2 3 1).
    const plane = (zNear: number, zFar: number): THREE.Mesh => {
      const g = new THREE.BufferGeometry();
      g.setAttribute('INvertPosition', new THREE.Float32BufferAttribute([-13000, 0, zNear, -13000, 0, zFar, 13000, 0, zNear, 13000, 0, zFar], 3));
      g.setIndex([0, 1, 2, 2, 3, 1]);
      const m = new THREE.Mesh(g, new THREE.RawShaderMaterial({
        glslVersion: THREE.GLSL3, vertexShader: VERT, fragmentShader: FRAG, uniforms: this.uniforms,
        transparent: true, depthTest: false, depthWrite: false, side: THREE.DoubleSide,
        blending: THREE.CustomBlending, blendEquation: THREE.AddEquation, blendSrc: THREE.SrcAlphaFactor, blendDst: THREE.OneMinusSrcAlphaFactor,
        blendEquationAlpha: THREE.AddEquation, blendSrcAlpha: THREE.OneFactor, blendDstAlpha: THREE.ZeroFactor,
      }));
      m.frustumCulled = false;
      return m;
    };
    this.back = plane(0, -10000);
    this.front = plane(2500, 0);
  }

  /** The theme's water fields and the SuperBundle water bitmaps. */
  async load(env: WaterEnv | null): Promise<void> {
    this.env = env;
    const [bump, normal, fresnel] = await Promise.all([
      bitmap('xom/DataLinux/Bundles/SuperBundle/Waterbump.json', THREE.RepeatWrapping),
      bitmap('xom/DataLinux/Bundles/SuperBundle/WaterNMB.json', THREE.RepeatWrapping),
      bitmap('xom/DataLinux/Bundles/SuperBundle/Water_Fresnel_RGB.json', THREE.RepeatWrapping),
    ]);
    this.uniforms._BumpMap.value = bump;
    this.uniforms._NormalMap.value = normal;
    this.uniforms._Fresnel.value = fresnel;
  }

  /** Per frame: the water level (world y), the game camera (matrices updated) and the frame
   * counter (`XomGetSystemTimeMilli` = frame / 60 s). */
  update(level: number, camera: THREE.PerspectiveCamera, reflection: THREE.Texture, frame: number): void {
    const e = this.env;
    if (!e) return;
    const u = this.uniforms;
    (u.ModelMatrix.value as THREE.Matrix4).makeTranslation(0, level, 0);
    const vp = (u.ViewProjM.value as THREE.Matrix4).multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    (u.ViewMatrix.value as THREE.Matrix4).copy(camera.matrixWorldInverse);
    const s = e.m_WaveScale, sp = e.m_WaveSpeed, t = waveTime(frame);
    (u._WaveScale4.value as THREE.Vector4).set(s.x, s.y, s.z, s.w);
    // fmod((float)(t · (float)(speed · scale)), 1.0) per component; C fmod keeps the sign.
    const off = (k: 'x' | 'y' | 'z' | 'w'): number => Math.fround(Math.fround(t * Math.fround(sp[k] * s[k])) % 1);
    (u._WaveOffset.value as THREE.Vector4).set(off('x'), off('y'), off('z'), off('w'));
    (u._WaterColor.value as THREE.Vector4).set(e.m_DiffuseColor.x, e.m_DiffuseColor.y, e.m_DiffuseColor.z, 1);
    u.fogStart.value = e.m_FogStart;
    u.fogEnd.value = e.m_FogEnd;
    (u.fogColor.value as THREE.Vector3).set(e.m_FogColor.x, e.m_FogColor.y, e.m_FogColor.z);
    const m = vp.elements;
    // Column-major: clip y = m[1]·x + m[5]·y + m[9]·z + m[13], clip w = m[3]·x + m[7]·y + m[11]·z + m[15].
    const ndc = (z: number): number => ((m[5] * level + m[9] * z + m[13]) / (m[7] * level + m[11] * z + m[15]) + 1) * 0.5;
    u.waterLevel.value = ndc(0);
    u.frontLevel.value = ndc(2500);
    u._ReflectionTex.value = reflection;
  }
}
